import { App, Button, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useAppStore } from '../../../store/store';
import { isActivateTotpPromoSuppressedPath } from '../../../utils/suppressActivateTotpPromo';
import { isTotpPromoEligibleRole } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';
import TotpForm from '../../Forms/TotpForm';

/**
 * Overlay component to prompt users to enable Two-Factor Authentication (TOTP).
 * - Modal is non-closable by user (except via provided actions).
 * - Shows TOTP benefits and stats.
 * - Controlled by app state: `appEvents.activateTotp`.
 */
export default function ActivateTotpOverlay() {
  const { pathname } = useLocation();
  // Internationalization hook for text.
  const { t } = useTranslation();

  // Ant Design modal/notification API from context.
  const { modal, notification } = App.useApp();
  // Refetches the authenticated user (e.g. isTotp) after a successful mandatory setup.
  const { getUserInfo } = useAuth();

  // Modal open state and close handler from app global store.
  const authenticatedUser = useAppStore((s) => s.authenticatedUser);
  const isOpen = useAppStore((s) => s.appEvents.activateTotp.isOpen);
  const isMandatory = useAppStore((s) => s.appEvents.activateTotp.isMandatory);
  const close = useAppStore((s) => s.appEvents.activateTotp.close);
  const remindDays = useAppStore((s) => s.appEvents.activateTotp.remindDays);
  const remindForDays = useAppStore((s) => s.appEvents.activateTotp.remindForDays);
  const setNeverShowAgain = useAppStore((s) => s.appEvents.activateTotp.setNeverShowAgain);

  // Opens secondary modal to complete TOTP setup.
  const handleOpenTotpSetup = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('modal.totpForm.title'),
      content: (
        <TotpForm
          isSetupOnly={false}
          // On close: destroy modal & close overlay (only reachable in non-mandatory mode; see closable prop below)
          onClose={() => {
            modalInstance.destroy();
            close();
          }}
          onSuccess={() => {
            modalInstance.destroy();
            close();
            // Refresh authenticatedUser.isTotp so the mandatory gate does not reopen on next check
            void getUserInfo();
          }}
          // On temporary TOTP expiration: destroy, close, show error
          onTemporaryTotpExpired={() => {
            modalInstance.destroy();
            close();
            notification.error({ title: t('errors.temporaryTotpExpired') });
          }}
        />
      ),
      // Mandatory mode: user must complete setup, no dismissing the setup dialog either.
      closable: !isMandatory,
      maskClosable: !isMandatory,
      keyboard: !isMandatory,
      footer: null,
      width: 560,
      zIndex: 10010,
    });
  }, [close, getUserInfo, isMandatory, modal, notification, t]);

  /** Confirmation before permanently opting out of 2FA (same pattern as `handleOpenTotpSetup`). */
  const handleOpenSkipForeverConfirm = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('modal.activateTotpOverlay.skipForeverConfirmTitle'),
      icon: null,
      centered: true,
      width: 560,
      zIndex: 10005,
      content: (
        <div className="flex flex-col gap-3 pb-4 text-sm leading-relaxed">
          <p>{t('modal.activateTotpOverlay.skipForeverConfirmBody1')}</p>
          <p className="font-medium">{t('modal.activateTotpOverlay.skipForeverConfirmBody2')}</p>
        </div>
      ),
      footer: (
        <div className="flex w-full flex-row flex-wrap items-center justify-between gap-2">
          <Button type="default" onClick={() => modalInstance.destroy()}>
            {t('common.cancel')}
          </Button>
          <Space wrap className="justify-end">
            <Button
              danger
              type="primary"
              onClick={() => {
                setNeverShowAgain(true);
                close();
                modalInstance.destroy();
              }}
            >
              {t('modal.activateTotpOverlay.skipForeverConfirmDecline')}
            </Button>
            <Button
              type="primary"
              className="border-green-dark! bg-green-dark! hover:border-green-dark/80! hover:bg-green-dark/80!"
              onClick={() => {
                modalInstance.destroy();
                handleOpenTotpSetup();
              }}
            >
              {t('modal.activateTotpOverlay.skipForeverConfirmEnableNow')}
            </Button>
          </Space>
        </div>
      ),
    });
  }, [close, handleOpenTotpSetup, modal, setNeverShowAgain, t]);

  // Reader / External never see this overlay (defensive; AuthContext also gates opening).
  if (!isTotpPromoEligibleRole(authenticatedUser?.role)) {
    return null;
  }

  // Viewer + protected-file gate: never cover anti-capture / pre-viewer flows (defensive).
  if (isActivateTotpPromoSuppressedPath(pathname)) {
    return null;
  }

  // Modal styling overrides.
  const modalStyles = {
    mask: { backdropFilter: 'blur(5px)' },
    content: { padding: 0, background: '#262626' },
    body: { padding: '24px 24px 8px' },
    footer: {
      marginTop: 0,
      padding: '8px 24px 24px',
      borderTop: 'none',
    },
  };

  return (
    <Modal
      open={isOpen}
      title={null}
      closable={false} // User can't close directly
      mask={{ closable: false }} // Mask can't be clicked to close
      keyboard={false} // Can't close with Escape key
      centered
      width={500}
      zIndex={9999}
      // Modal footer holds the actions and info note
      footer={
        <div className="flex w-full flex-col gap-3">
          {/* Activate button: launches TOTP setup modal */}
          <Button type="primary" size="large" block onClick={handleOpenTotpSetup}>
            {t('modal.activateTotpOverlay.actionActivate')}
          </Button>
          {!isMandatory && (
            <>
              {/* 'Remind me later': hide overlay for remindDays (from gracePeriodEnds, capped at 7) */}
              <Button size="large" block type="default" onClick={() => remindForDays(remindDays)}>
                <span className="font-medium">
                  {t('modal.activateTotpOverlay.actionRemind', { count: remindDays })}
                </span>
              </Button>
              {/* Opens confirmation before permanent opt-out */}
              <Button size="large" danger block onClick={handleOpenSkipForeverConfirm}>
                {t('modal.activateTotpOverlay.actionSkipForever')}
              </Button>
            </>
          )}
          {/* Extra note under action buttons */}
          <p className="text-gray-dark text-center text-xs leading-snug">
            {isMandatory
              ? t('modal.activateTotpOverlay.footerMandatory')
              : t('modal.activateTotpOverlay.footer')}
          </p>
        </div>
      }
      styles={modalStyles}
      className="[&_.ant-modal-content]:overflow-hidden [&_.ant-modal-content]:rounded-lg"
      data-testid="activate-totp-overlay"
    >
      <div className="text-gray-dark flex flex-col gap-4">
        {/* HEADER: Shield icon & Title */}
        <div className="flex flex-row items-start gap-4">
          {/* Icon container */}
          <div className="bg-blue-dark/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl">
            <Icon name="ShieldCheckIcon" className="text-blue-dark h-7 w-7" aria-hidden="true" />
          </div>
          {/* Title and badge */}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span
              className={
                isMandatory
                  ? 'bg-red/10 text-red inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide uppercase'
                  : 'bg-blue-dark/10 text-blue-dark inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide uppercase'
              }
            >
              {isMandatory
                ? t('modal.activateTotpOverlay.badgeMandatory')
                : t('modal.activateTotpOverlay.badge')}
            </span>
            <h2 className="text-blue-dark text-lg leading-tight font-semibold">
              {isMandatory
                ? t('modal.activateTotpOverlay.titleMandatory')
                : t('modal.activateTotpOverlay.title')}
            </h2>
          </div>
        </div>

        {/* DESCRIPTIONS */}
        <p className="text-gray-dark pt-4 text-sm leading-relaxed">
          {isMandatory
            ? t('modal.activateTotpOverlay.description1Mandatory')
            : t('modal.activateTotpOverlay.description1')}
        </p>
        <p className="text-gray-dark text-sm leading-relaxed">
          {t('modal.activateTotpOverlay.description2')}
        </p>

        {/* Stats area: two columns */}
        <div className="grid h-24 grid-cols-2 gap-4 py-2">
          {/* Stat 1 */}
          <div className="bg-blue-dark/10 rounded-lg px-3 py-3 text-center">
            <div className="text-blue text-2xl font-bold tracking-tight">
              {t('modal.activateTotpOverlay.stat1Value')}
            </div>
            <div className="text-gray-dark mt-1 text-xs leading-snug">
              {t('modal.activateTotpOverlay.stat1Label')}
            </div>
          </div>
          {/* Stat 2 */}
          <div className="bg-blue-dark/10 rounded-lg px-3 py-3 text-center">
            <div className="text-blue text-2xl font-bold tracking-tight">
              {t('modal.activateTotpOverlay.stat2Value')}
            </div>
            <div className="text-gray-dark mt-1 text-xs leading-snug">
              {t('modal.activateTotpOverlay.stat2Label')}
            </div>
          </div>
        </div>

        {/* Feature List with check marks */}
        <div className="border-gray-medium rounded-lg border bg-white px-4 py-6">
          {/* Features headline */}
          <p className="text-blue-dark mb-3 text-xs font-semibold tracking-wide uppercase">
            {t('modal.activateTotpOverlay.featuresTitle')}
          </p>
          <ul className="flex flex-col gap-2.5">
            {/* Feature 1 */}
            <li className="text-gray-dark flex flex-row items-start gap-2 text-sm">
              <Icon
                name="CircleCheckIcon"
                className="text-green-dark mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>{t('modal.activateTotpOverlay.feature1')}</span>
            </li>
            {/* Feature 2 */}
            <li className="text-gray-dark flex flex-row items-start gap-2 text-sm">
              <Icon
                name="CircleCheckIcon"
                className="text-green-dark mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>{t('modal.activateTotpOverlay.feature2')}</span>
            </li>
            {/* Feature 3 */}
            <li className="text-gray-dark flex flex-row items-start gap-2 text-sm">
              <Icon
                name="CircleCheckIcon"
                className="text-green-dark mt-0.5 h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <span>{t('modal.activateTotpOverlay.feature3')}</span>
            </li>
          </ul>
        </div>
      </div>
    </Modal>
  );
}
